/**
 * Milo follows the cursor — the part that is about the camera rather than about a
 * component.
 *
 * # Why the CAMERA and not the head
 *
 * The head is a `<model-viewer>` inside the vendored package's shadow root (deviation
 * 28, `docs/AI.md` § 20), and model-viewer exposes no bones, no morph targets and no
 * eyes: there is nothing in its API that can turn a neck. What it does expose is the
 * camera, as `camera-orbit`, and a small change of the orbit's azimuth and polar angle
 * puts the face where the cursor is — the same trick as walking around somebody rather
 * than asking them to turn. model-viewer interpolates every orbit change on its own
 * (its `interpolationDecay`), so this sets a target and never animates anything.
 *
 * The package is never edited (deviation 28) and this reaches into its shadow root
 * READ-ONLY except for that one attribute. A version that stops creating a
 * `<model-viewer>`, or renames it, is not an error here: there is simply no gaze, and
 * the head goes on doing everything else.
 *
 * # What it is not
 *
 * It does not touch the animation clips and does not know they exist. A reaction —
 * Success, Frustrated, Smile — animates the FACE; the camera stands somewhere while it
 * plays. The two were watched together in a browser and do not fight, so a reaction is
 * not a reason to stop following.
 */
import { onBeforeUnmount } from 'vue'

/**
 * Where the package parks its camera. **These mirror `milo-element.js`'s own
 * `camera-orbit` and `field-of-view` and must be re-read from it on every upgrade of
 * `public/milo/<version>/`** — the gaze is an offset from this pose, so a package that
 * frames the head differently and a constant that still says 10/87 is a head looking
 * past the cursor by however much the two disagree.
 *
 * `radius` travels as the string the package writes, because `125%` is a fraction of
 * the model's framing distance and not a length this file has any business computing.
 */
const BASE_ORBIT = { theta: 10, phi: 87, radius: '125%' } as const

/**
 * How far the head may turn, in degrees off the base pose.
 *
 * Small on purpose. Milo is drawn in a 40 px circle that crops to the face
 * (`MiloHead.vue`, `HEAD_SCALE`), so a camera that swings far enough to be dramatic
 * swings the crown and the ear out of the circle instead. Fourteen and eight are what
 * still reads as "he looked over" at 40 px and does not break the crop at 240 px.
 */
const YAW_DEGREES   = 14
const PITCH_DEGREES = 8

/**
 * The distance at which the head is turned as far as it goes.
 *
 * A fraction of the viewport was the other candidate and is worse: the same gesture
 * would mean different things on a laptop and on the 27-inch screen the desk actually
 * has, and on the wide one Milo would barely move for a cursor at the far edge. A
 * length is the honest unit — the cursor is 600 px away or it is not.
 */
const REACH_PX = 600

/** How long the head keeps looking at a cursor that has stopped. */
const SETTLE_MS = 2500

/**
 * Where the gaze is switched off entirely.
 *
 * Reduced motion for the obvious reason, and a coarse pointer because there is no
 * cursor to follow: an iPad reports the last place a finger touched, so a head that
 * followed it would hold a stare at wherever the desk last pressed and never let go.
 * Both are watched live — somebody who turns reduced motion on wants it on now.
 */
const NO_GAZE = '(prefers-reduced-motion: reduce), (pointer: coarse)'

/** Fast where the cursor is near, gently saturating at the limit. */
const ease = (n: number): number => Math.sign(n) * Math.sin(Math.min(Math.abs(n), 1) * Math.PI / 2)

/**
 * Verified in a browser against this package, because the direction is not guessable:
 * model-viewer's azimuth turns the CAMERA, so a camera moved to the right shows the
 * model's left cheek and reads as a head looking LEFT. The face follows the cursor when
 * the camera goes the other way, hence the minus. The polar angle is the same argument
 * standing up: a camera below the chin reads as a head looking up, so a cursor above
 * the head asks for a LARGER phi.
 */
const orbitFor = (nx: number, ny: number): string => {
    const theta = BASE_ORBIT.theta - ease(nx) * YAW_DEGREES
    const phi   = BASE_ORBIT.phi   - ease(ny) * PITCH_DEGREES

    return `${theta.toFixed(2)}deg ${phi.toFixed(2)}deg ${BASE_ORBIT.radius}`
}

export interface MiloGaze {
    /** Follow the cursor with this head. Called again for a head that was re-created. */
    follow(host: HTMLElement | null): void
    /** Stop following and put the camera back where the package left it. */
    release(): void
}

/**
 * One gaze per mounted head.
 *
 * Both heads on the page (the top bar and the drawer's header) run their own, because
 * each measures from its OWN centre: the same cursor is up and to the left of one and
 * straight ahead of the other, and a shared computation would have them looking at the
 * same point rather than at the same cursor. The cost is one passive `pointermove`
 * listener each, throttled to a frame.
 */
export const useMiloGaze = (): MiloGaze => {
    if (typeof window === 'undefined') return { follow: () => {}, release: () => {} }

    const blocked = window.matchMedia(NO_GAZE)

    let host:    HTMLElement | null = null
    let viewer:  Element | null     = null
    let frame:   number | null      = null
    let settle:  ReturnType<typeof setTimeout> | null = null
    let pointer: { x: number, y: number } | null = null
    let bound    = false

    /**
     * The viewer is created by the package and REPLACED by it — on a new `src`, and on
     * every reconnection of the element. Holding one is therefore holding something
     * that can go stale without a word, so the answer is re-asked the moment the one in
     * hand has left the document.
     */
    const currentViewer = (): Element | null => {
        if (!host?.isConnected) return null

        if (!viewer?.isConnected) viewer = host.shadowRoot?.querySelector('model-viewer') ?? null

        return viewer
    }

    const cancelFrame = () => {
        if (frame !== null) cancelAnimationFrame(frame)

        frame = null
    }

    const cancelSettle = () => {
        if (settle !== null) clearTimeout(settle)

        settle = null
    }

    const toBase = () => {
        cancelFrame()
        cancelSettle()
        pointer = null
        currentViewer()?.setAttribute('camera-orbit', orbitFor(0, 0))
    }

    const apply = () => {
        frame = null

        const target = currentViewer()

        if (!target || !pointer || !host) return

        // The element's box is the STAGE, which is larger than the visible circle and
        // sits high in it (`MiloHead.vue`, `HEAD_RISE`) — so its centre is a few pixels
        // above the circle's. That is the right few pixels: the stage is placed so that
        // the FACE lands in the circle, and the face is what is being asked to look.
        const box = host.getBoundingClientRect()

        if (box.width === 0 || box.height === 0) return

        const nx = (pointer.x - (box.left + box.width  / 2)) / REACH_PX
        const ny = (pointer.y - (box.top  + box.height / 2)) / REACH_PX

        target.setAttribute('camera-orbit', orbitFor(nx, ny))
    }

    const onMove = (event: PointerEvent) => {
        pointer = { x: event.clientX, y: event.clientY }

        cancelSettle()
        // A cursor that has stopped is not a cursor anybody is pointing with, and a
        // head frozen mid-turn looks stuck rather than attentive.
        settle = setTimeout(toBase, SETTLE_MS)

        if (frame === null) frame = requestAnimationFrame(apply)
    }

    /** The cursor left the page, or the window lost focus: there is nothing to look at. */
    const onLeave = () => toBase()

    const listen = () => {
        if (bound) return

        bound = true
        document.addEventListener('pointermove', onMove, { passive: true })
        document.addEventListener('pointerleave', onLeave)
        window.addEventListener('blur', onLeave)
    }

    const silence = () => {
        if (!bound) return

        bound = false
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerleave', onLeave)
        window.removeEventListener('blur', onLeave)
    }

    const onBlockedChange = () => {
        if (blocked.matches) {
            silence()
            toBase()
        } else if (host) {
            listen()
        }
    }

    blocked.addEventListener('change', onBlockedChange)

    const release = () => {
        silence()
        cancelFrame()
        cancelSettle()
        pointer = null
        host    = null
        viewer  = null
    }

    onBeforeUnmount(() => {
        release()
        blocked.removeEventListener('change', onBlockedChange)
    })

    return {
        follow: (element: HTMLElement | null) => {
            // A new element is a new shadow root; the viewer in hand belongs to the old
            // one and would be written to for ever without ever being on screen.
            viewer = null
            host   = element

            if (!element) {
                silence()

                return
            }

            if (!blocked.matches) listen()
        },
        release,
    }
}
